/**
 * TopMoversStrip — "Top Movers this week" strip for the Stage (home) tab.
 *
 * Shows the entities with the biggest 7-day price move (both directions,
 * server-computed via GET /api/entities/top-movers). Behind the PostHog
 * `top-movers` feature flag (default on) so it can be toggled/A-B tested
 * without a redeploy. Tapping a card opens the entity's profile.
 */
import { useEffect, useState } from 'react';
import { formatFranc } from '../lib/utils';
import { entityAPI } from '../lib/api';
import { useFeatureFlag } from '../lib/useFeatureFlag';
import EntityAvatar from './EntityAvatar';
import { TrendingUp, TrendingDown } from 'lucide-react';
import { posthogCapture } from '../posthog.config';

export default function TopMoversStrip({ onEntityClick }) {
  const enabled = useFeatureFlag('top-movers', true);
  const [movers, setMovers] = useState(null);

  useEffect(() => {
    if (!enabled || movers) return;
    let cancelled = false;
    entityAPI.topMovers({ limit: 10 })
      .then((res) => { if (!cancelled) setMovers(res.data?.movers || []); })
      .catch(() => { if (!cancelled) setMovers([]); });
    return () => { cancelled = true; };
  }, [enabled, movers]);

  if (!enabled || !movers || movers.length === 0) return null;

  const open = (m) => {
    posthogCapture('top_movers_tap', { entity_id: m.id, pct_change: m.pct_change });
    onEntityClick?.(m.id);
  };

  return (
    <div className="mt-4 rounded-xl border border-border/60 bg-card p-3" data-testid="top-movers-strip">
      <p className="text-xs font-semibold flex items-center gap-1.5 mb-2">
        <TrendingUp className="w-3.5 h-3.5 text-primary" />
        Top Movers this week
      </p>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {movers.map((m) => {
          const up = (m.pct_change || 0) > 0;
          return (
            <button
              key={m.id}
              onClick={() => open(m)}
              className="flex items-center gap-2 min-w-[164px] px-2.5 py-2 rounded-lg border border-border/60 bg-background text-left hover:shadow-sm transition-shadow"
              data-testid={`top-mover-card-${m.id}`}
            >
              <EntityAvatar entity={m} size={32} />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium truncate">{m.name}</p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className={`text-[10px] font-semibold tabular-nums flex items-center gap-0.5 ${up ? 'text-green-500' : 'text-red-500'}`}>
                    {up ? <TrendingUp className="w-2.5 h-2.5" /> : <TrendingDown className="w-2.5 h-2.5" />}
                    {up ? '+' : ''}{m.pct_change}%
                  </span>
                  <span className="text-[10px] text-muted-foreground tabular-nums">
                    {formatFranc(m.pricing?.price)}
                  </span>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
